import { useMemo } from 'react';
import { Stack, TableRow, TableCell, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IOrderDocument, ORDER_DOCUMENT_TYPES } from '@/types/order-document';
import { getExtension } from '@/utils/helpers/common';
import PDFViewer from '@/components/media/preview-documents/preview-documents';
import DownloadButton from './salesOrder-document-download';
import { getDocumentTypeChip } from '../../utils/order-document.utils';
import moment from 'moment';

type Props = {
  row: IOrderDocument;
};

export function OrderDocumentDetailTableRow({ row }: Props) {
  const { t } = useTranslation();

  const documentTypeDisplay = useMemo(
    () => getDocumentTypeChip(row.documentType, t),
    [row.documentType, t]
  );

  const fileNameDisplay = useMemo(() => {
    if (row?.file?.name) {
      return row.file.name;
    }
    return '-';
  }, [row?.file?.name]);

  const dateDisplay = useMemo(() => {
    if (row.documentDate) {
      return moment(row.documentDate).utc().format('L');
    }
    if (row.createdAt) {
      return moment(row.createdAt).utc().format('L');
    }
    return '-';
  }, [row.documentDate, row.createdAt]);

  const userDisplay = useMemo(() => {
    if (row.submittedBy) {
      const firstName = row.submittedBy.firstName || '-';
      const lastName = row.submittedBy.lastName || '';
      return lastName ? `${firstName} ${lastName}` : firstName;
    }
    return '-';
  }, [row.submittedBy]);

  const metadataDisplay = useMemo(() => {
    const metadata = [];

    if (
      row.number &&
      row.documentDate &&
      row.amount !== undefined &&
      row.documentType === ORDER_DOCUMENT_TYPES.PURCHASE_ORDER
    ) {
      const documentDateDisplay = `${row.documentDate ? `${moment(row.documentDate).utc().format('L')}` : ''}`;
      const amountDisplay = `${row.amount !== undefined ? ` · ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(row.amount)}` : ''}`;
      metadata.push(`${t('OC')}: ${row.number}`);
      metadata.push(`${documentDateDisplay} ${amountDisplay}`);
    }

    if (metadata.length === 0) {
      return '-';
    }

    return (
      <Stack spacing={0.5}>
        {metadata.map((item, index) => (
          <Typography key={index} variant="caption" color="text.secondary">
            {item}
          </Typography>
        ))}
      </Stack>
    );
  }, [row.number, row.amount, row.documentDate, t, row.documentType]);

  return (
    <TableRow hover>
      <TableCell>{documentTypeDisplay}</TableCell>

      <TableCell>
        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
          {fileNameDisplay}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2">{dateDisplay}</Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2">{userDisplay}</Typography>
      </TableCell>

      <TableCell>{metadataDisplay}</TableCell>

      <TableCell align="center">
        <Stack direction="row" spacing={0.5} justifyContent="center">
          {/* Preview Button */}
          {row?.file?.url && (
            <PDFViewer
              file={row.file.url}
              fileName={row.file.name || ''}
              fileSize={row.file.size || 0}
            />
          )}

          {/* Download Button */}
          {row?.file?.url && (
            <DownloadButton
              fileUrl={row.file.url}
              fileName={row.file.name || `document.${getExtension(row.file.url)}`}
              icon={'material-symbols:download'}
              color="default"
            />
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
